import { Box, MessageBlock } from '@deckee/deck-hand';
import React, { FC } from 'react';
import styled from 'styled-components';
import Shimmer from '../../../../shared/Shimmer/Shimmer';
import {
  getWeatherPrecisIcon,
  ReactSvg,
} from '../../../../shared/vector-images';
import { type WeatherForecastCurrentConditionsViewModel } from './formatCurrentConditions';
import { useWeatherForecastCurrentConditions } from '../../hooks/useWeatherForecastCurrentConditions';
import CollapsibleCard from '../CollapsibleCard/CollapsibleCard';
import MetricBox from './MetricBox';

const MetricGrid = styled(Box)`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
`;

const TemperatureValue = styled(Box)`
  font-size: 50px;
  font-weight: 700;
  line-height: 1;
  color: ${({ theme }) => theme.colors.black};
`;

export interface CurrentConditionsProps {
  latitude: number;
  longitude: number;
}

interface CurrentConditionsContentProps {
  data: WeatherForecastCurrentConditionsViewModel;
}

const CurrentConditionsContent: FC<CurrentConditionsContentProps> = ({
  data,
}) => (
  <>
    <Box display="flex" alignItems="flex-start" mb={2}>
      {data.weatherIcon ? (
        <Box flex="0 0 60px" mr={2}>
          <ReactSvg
            toDisplay={getWeatherPrecisIcon(data.weatherIcon)}
            width={60}
            height={60}
          />
        </Box>
      ) : (
        <Box flex="0 0 60px" mr={2} minHeight="60px" />
      )}

      <Box display="flex" alignItems="flex-start" flex="1 1 auto">
        <TemperatureValue mr={3}>
          {data.temperatureDisplay ?? '—'}
        </TemperatureValue>

        <Box fontSize={1} color="black" lineHeight="1.2">
          {data.feelsLikeDisplay && (
            <Box mb="2px">Feels Like {data.feelsLikeDisplay}</Box>
          )}
          {data.highTempDisplay && (
            <Box color="danger" mb="2px">
              High {data.highTempDisplay}
            </Box>
          )}
          {data.lowTempDisplay && (
            <Box color="secondary">Low {data.lowTempDisplay}</Box>
          )}
        </Box>
      </Box>
    </Box>

    {data.description && (
      <Box fontSize={1} color="black" lineHeight="1.3" mb={3}>
        {data.description}
      </Box>
    )}

    <MetricGrid data-testid="weather-forecast-current-conditions-metrics">
      {data.metrics.map((metric) => (
        <MetricBox
          key={metric.label}
          label={metric.label}
          value={metric.value}
        />
      ))}
    </MetricGrid>
  </>
);

const CurrentConditionsLoading: FC = () => (
  <Box data-testid="weather-forecast-current-conditions-loading" aria-busy>
    <Shimmer on>
      <Box minHeight="60px" mb={2} />
    </Shimmer>
    <Shimmer on>
      <Box minHeight="36px" mb={3} />
    </Shimmer>
    <MetricGrid>
      {['wind', 'humidity', 'pressure', 'uv', 'dew', 'rain'].map(
        (metricKey) => (
          <Box key={`loading-metric-${metricKey}`} minHeight="56px">
            <Shimmer on>
              <Box minHeight="56px" />
            </Shimmer>
          </Box>
        ),
      )}
    </MetricGrid>
  </Box>
);

const CurrentConditions: FC<CurrentConditionsProps> = ({
  latitude,
  longitude,
}) => {
  const { data, isLoading, error } = useWeatherForecastCurrentConditions(
    latitude,
    longitude,
  );

  return (
    <CollapsibleCard
      title="Current conditions"
      testId="weather-forecast-current-conditions"
      analyticsCard="current conditions"
    >
      {error && (
        <MessageBlock type={error.type} size="small" mb={2}>
          {error.message}
        </MessageBlock>
      )}

      {isLoading && !error && <CurrentConditionsLoading />}

      {!isLoading && !error && data && <CurrentConditionsContent data={data} />}
    </CollapsibleCard>
  );
};

export default CurrentConditions;
